import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';

import {
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {useOrganizationAccounts} from '../hooks/useOrganizationAccounts.ts';
import {useOrganizationAccountsSearch} from '../hooks/useOrganizationAccountsSearch.ts';
import {useOrganizationAccountsSelection} from '../hooks/useOrganizationAccountsSelection.ts';
import {useOrganizationAccountsTableColumns} from '../hooks/useOrganizationAccountsTableColumns.tsx';
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {OrganizationAccount} from '../types';

export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
} = {}) => {

    const {allowedActions} = useVerifiedPermissions()
    const {id: organizationId} = useParams();

    const navigate = useNavigate();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useOrganizationAccounts(organizationId ? {organizationId} : {});

    const {
        columns,
    } = useOrganizationAccountsTableColumns();

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useOrganizationAccounts owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useOrganizationAccountsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useOrganizationAccountsSelection();

    // Checkboxes appear only for an operator who can actually remove a
    // membership. Offering selection to someone with no action to apply to it
    // is a dead affordance.
    let selectedRecords: OrganizationAccount[] = [];
    if (allowedActions.includes(rbacActions.DeleteOrganizationAccount)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that this organization has no accounts at all.
    const noRecordsText = keyword
        ? `No organization accounts match "${keyword}" in the records loaded so far.`
        : 'No organization accounts yet.';

    return (
        <Stack m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {extraTools}

            {isError && (
                <Alert color='red' title='Could not load organization accounts'>
                    The console API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) =>
                    navigate(`/accounts/${encodeURIComponent((record as OrganizationAccount).accountId)}`)}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='organization account'
            />
        </Stack>
    );
};
